import { ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX, type RefObject } from 'react';

import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/utils.js';

import {
  nextStepLabel,
  type NextStepId,
  type NextStepModel,
} from './nextSteps.js';

/** One list for every run, so the stored state stays bounded however many runs a user opens. */
const COLLAPSED_RUNS_KEY = 'quality.runNextSteps.collapsed';
const MAX_COLLAPSED_RUNS = 200;

/** Storage can be missing, throw (private window, blocked site data) or hold junk; all read as empty. */
function readCollapsedRuns(): string[] {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(COLLAPSED_RUNS_KEY) ?? '[]',
    );
    return Array.isArray(stored)
      ? stored.filter((runId): runId is string => typeof runId === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeCollapsedRuns(runIds: string[]): void {
  try {
    localStorage.setItem(COLLAPSED_RUNS_KEY, JSON.stringify(runIds));
  } catch {
    return;
  }
}

/** Remembers the run as the most recent one, dropping the oldest past the cap. */
function rememberCollapsed(runId: string): void {
  const others = readCollapsedRuns().filter((stored) => stored !== runId);
  writeCollapsedRuns([...others, runId].slice(-MAX_COLLAPSED_RUNS));
}

function forgetCollapsed(runId: string): void {
  writeCollapsedRuns(readCollapsedRuns().filter((stored) => stored !== runId));
}

/**
 * The next-step strip under the run header: one sentence on how the run ended and the two or three
 * actions that fit it, with "All actions" opening the full run actions panel. Once a step from it
 * succeeds the strip collapses to the summary line for that run and moves focus to "All actions";
 * "Show next steps" expands it again. "All actions" keeps its place in the tree across the collapse.
 * Mount the strip with `key={runId}` so the remembered state follows a change of run.
 */
export function RunNextSteps({
  runId,
  model,
  busy,
  onStep,
  actionsOpen,
  onActionsOpenChange,
}: {
  runId: string;
  model: NextStepModel;
  busy: string | null;
  /** Resolves true when the step succeeded. */
  onStep: (step: NextStepId) => Promise<boolean>;
  actionsOpen: boolean;
  onActionsOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsedRuns().includes(runId),
  );
  const allActionsRef = useRef<HTMLButtonElement>(null);
  const firstStepRef = useRef<HTMLButtonElement>(null);
  /** Set by "Show next steps", which unmounts itself: focus moves to the first step once it renders. */
  const focusFirstStep = useRef(false);

  useEffect(() => {
    if (collapsed || !focusFirstStep.current) return;
    focusFirstStep.current = false;
    firstStepRef.current?.focus();
  }, [collapsed]);

  const runStep = async (step: NextStepId): Promise<void> => {
    if (!(await onStep(step))) return;
    rememberCollapsed(runId);
    setCollapsed(true);
    allActionsRef.current?.focus();
  };

  const expand = (): void => {
    forgetCollapsed(runId);
    focusFirstStep.current = true;
    setCollapsed(false);
  };

  return (
    <section
      role="region"
      aria-label="Next steps for this run"
      className="flex w-full flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-3"
    >
      <p
        className={cn(
          'text-sm font-medium text-foreground',
          collapsed ? 'mr-auto' : 'w-full',
        )}
      >
        {model.summary}
      </p>
      {collapsed ? (
        <Button variant="ghost" size="sm" onClick={expand}>
          Show next steps
        </Button>
      ) : (
        model.steps.map((step, index) => (
          <Button
            key={step}
            ref={index === 0 ? firstStepRef : undefined}
            variant={index === 0 ? 'default' : 'outline'}
            size="sm"
            onClick={() => void runStep(step)}
          >
            {busy === step ? (
              <Loader2Icon className="animate-spin" aria-hidden />
            ) : null}
            {nextStepLabel(step, model.counts)}
          </Button>
        ))
      )}
      <AllActionsButton
        buttonRef={allActionsRef}
        open={actionsOpen}
        onToggle={() => onActionsOpenChange(!actionsOpen)}
      />
    </section>
  );
}

function AllActionsButton({
  buttonRef,
  open,
  onToggle,
}: {
  buttonRef: RefObject<HTMLButtonElement | null>;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="sm"
      aria-expanded={open}
      aria-controls="quality-run-actions-panel"
      onClick={onToggle}
    >
      All actions
      <ChevronDownIcon
        className={open ? 'rotate-180' : undefined}
        aria-hidden
      />
    </Button>
  );
}
